import React from 'react';
import { CheckCircle, MapPin, Calendar, Clock, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const ConfirmationCard = ({ bookingData, onConfirm, onCancel }: any) => {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="mb-4"
    >
      <div className="relative group">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl blur opacity-40 group-hover:opacity-60 transition duration-300"></div>
        
        <div className="relative bg-background dark:bg-slate-800 rounded-2xl p-6 border border-border dark:border-slate-700">
          {/* Header */}
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
              <CheckCircle size={24} className="text-white" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-foreground dark:text-slate-200">{t('confirmationCard.confirmBooking', 'Confirm booking')}</h4>
              <p className="text-sm text-muted-foreground">{t('confirmationCard.checkThatEverythingLooksCorrect', 'Check that everything looks correct')}</p>
            </div>
          </div>

          {/* Booking Details */}
          <div className="space-y-3 mb-6">
            {/* From Location */}
            <div className="flex items-start space-x-3 p-3 bg-muted/50 dark:bg-slate-900/50 rounded-xl">
              <MapPin size={20} className="text-green-600 dark:text-green-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-medium text-muted-foreground dark:text-muted-foreground">{t('confirmationCard.fra', 'Fra')}</p>
                <p className="text-sm font-semibold text-foreground dark:text-slate-200">{bookingData.from}</p>
              </div>
            </div>

            {/* To Location */}
            <div className="flex items-start space-x-3 p-3 bg-muted/50 dark:bg-slate-900/50 rounded-xl">
              <MapPin size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-medium text-muted-foreground dark:text-muted-foreground">{t('confirmationCard.til', 'Til')}</p>
                <p className="text-sm font-semibold text-foreground dark:text-slate-200">{bookingData.to}</p>
              </div>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-start space-x-3 p-3 bg-muted/50 dark:bg-slate-900/50 rounded-xl">
                <Calendar size={20} className="text-purple-600 dark:text-purple-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-muted-foreground dark:text-muted-foreground">{t('confirmationCard.dato', 'Dato')}</p>
                  <p className="text-sm font-semibold text-foreground dark:text-slate-200">{bookingData.date}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-muted/50 dark:bg-slate-900/50 rounded-xl">
                <Clock size={20} className="text-orange-600 dark:text-orange-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-muted-foreground dark:text-muted-foreground">{t('confirmationCard.tid', 'Tid')}</p>
                  <p className="text-sm font-semibold text-foreground dark:text-slate-200">{bookingData.time}</p>
                </div>
              </div>
            </div>

            {/* Passengers */}
            <div className="flex items-start space-x-3 p-3 bg-muted/50 dark:bg-slate-900/50 rounded-xl">
              <Users size={20} className="text-amber-600 dark:text-amber-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-medium text-muted-foreground dark:text-muted-foreground">{t('confirmationCard.passasjerer', 'Passasjerer')}</p>
                <p className="text-sm font-semibold text-foreground dark:text-slate-200">
                  {bookingData.passengers} {bookingData.passengers === 1 ? 'person' : 'personer'}
                </p>
              </div>
            </div>

            {/* Stops (if any) */}
            {bookingData.stops && bookingData.stops.length > 0 && (
              <div className="flex items-start space-x-3 p-3 bg-muted/50 dark:bg-slate-900/50 rounded-xl">
                <MapPin size={20} className="text-teal-600 dark:text-teal-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-muted-foreground dark:text-muted-foreground">{t('confirmationCard.stoppUnderveis', 'Stopp underveis')}</p>
                  <div className="space-y-1 mt-1">
                    {bookingData.stops.map((stop: any, idx: any) => (
                      <p key={stop.id} className="text-sm font-semibold text-foreground dark:text-slate-200">
                        {idx + 1}. {stop.location}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Flight Info (if provided) */}
            {bookingData.flightNumber && (
              <div className="flex items-start space-x-3 p-3 bg-sky-50 dark:bg-sky-900/20 rounded-xl border border-sky-200 dark:border-sky-700">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-lg bg-sky-500 flex items-center justify-center">
                    <span className="text-white text-lg">✈️</span>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-sky-600 dark:text-sky-400">{t('confirmationCard.flyinformasjon', 'Flyinformasjon')}</p>
                  <p className="text-sm font-semibold text-sky-800 dark:text-sky-200">
                    {bookingData.flightNumber}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="flex-1 py-3 px-4 bg-slate-200 dark:bg-slate-700 text-foreground dark:text-slate-300 rounded-xl font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition-all"
            >
              {t('confirmationCard.cancel', 'Cancel')}
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-green-500/50 transition-all duration-300"
            >
              ✓ Confirm booking
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ConfirmationCard;
